import { StandardEvents } from '@shopify/events';

/**
 * Progressive enhancement for `blocks/thcc-pdp-presentacion-info.liquid`.
 *
 * The block renders an accessible list (`ul.thcc-pdp-info`) with one row per variant. This module
 * copies each row's data into the matching `label.variant-option__button-label` of the `variant-picker`
 * in the same details column: detail ("50 mg c/u" + per-piece price in a separate span), a footer with
 * the price and the 4-cell level meter, and the "best price" tag. Then it marks the list as
 * `--integrada` (visually hidden, still read by screen readers). Data comes from `data-*` attributes.
 *
 * Matching (snippets/variant-main-picker.liquid): an input carries `data-variant-id` only for
 * combined listings, so we try that first, then the input `value` against the row's option1
 * (`data-valor`), and only when the product has a single option. Text is always set with
 * `textContent`; nothing is parsed as HTML.
 */

const DETAIL_CLASS = 'thcc-pdp-card__detalle';
const BEST_CLASS = 'thcc-pdp-card__mejor';
const FOOT_CLASS = 'thcc-pdp-card__pie';

/** @type {WeakMap<Element, MutationObserver>} */
const observers = new WeakMap();

/**
 * @param {HTMLElement} list
 * @returns {Element | null}
 */
function findPicker(list) {
  return list.closest('.product-details')?.querySelector('variant-picker') ?? null;
}

/**
 * @param {HTMLElement} list
 * @param {Element} picker
 * @returns {boolean} true when every row found its label
 */
function enhance(list, picker) {
  if (list.dataset.opciones !== '1') return false;

  const rows = /** @type {HTMLElement[]} */ ([...list.querySelectorAll('.thcc-pdp-info__fila')]);
  const inputs = /** @type {HTMLInputElement[]} */ ([...picker.querySelectorAll('input[type="radio"]')]);
  let matched = 0;

  for (const row of rows) {
    const input =
      inputs.find((candidate) => candidate.dataset.variantId === row.dataset.varianteId) ??
      inputs.find((candidate) => row.dataset.valor && candidate.value === row.dataset.valor);
    const label = input?.closest('label');
    if (!label) continue;
    matched += 1;

    const mg = row.dataset.mg ?? '';
    const perPiece = row.dataset.pp ?? '';
    const price = row.dataset.precio ?? '';
    const level = Number(row.dataset.nivel ?? 0);
    const best = row.querySelector('.thcc-pdp-info__insignia')?.textContent?.trim() ?? '';

    if (mg && !label.querySelector(`.${DETAIL_CLASS}`)) {
      const detail = document.createElement('span');
      detail.className = DETAIL_CLASS;
      detail.setAttribute('aria-hidden', 'true');
      detail.append(document.createTextNode(mg));
      if (perPiece) {
        const pp = document.createElement('span');
        pp.className = 'thcc-pdp-card__pp';
        pp.textContent = ` · ${perPiece}`;
        detail.append(pp);
      }
      label.append(detail);
    }

    if (price && !label.querySelector(`.${FOOT_CLASS}`)) {
      const foot = document.createElement('span');
      foot.className = FOOT_CLASS;
      foot.setAttribute('aria-hidden', 'true');
      const priceEl = document.createElement('span');
      priceEl.textContent = price;
      foot.append(priceEl);
      if (level >= 1 && level <= 4) {
        const meter = document.createElement('span');
        meter.className = 'thcc-pdp-card__barra';
        for (let i = 1; i <= 4; i += 1) {
          const cell = document.createElement('i');
          if (i <= level) cell.className = 'is-on';
          meter.append(cell);
        }
        foot.append(meter);
      }
      label.append(foot);
    }

    if (best && !label.querySelector(`.${BEST_CLASS}`)) {
      const tag = document.createElement('span');
      tag.className = BEST_CLASS;
      tag.setAttribute('aria-hidden', 'true');
      tag.textContent = best;
      label.append(tag);
    }
  }

  if (matched > 0 && matched === rows.length) list.classList.add('thcc-pdp-info--integrada');
  return matched === rows.length;
}

/**
 * Applies the enhancement to every list on the page and keeps it applied when Horizon morphs the picker.
 */
function run() {
  for (const list of document.querySelectorAll('ul.thcc-pdp-info')) {
    if (!(list instanceof HTMLElement)) continue;
    const picker = findPicker(list);
    if (!picker) continue;

    enhance(list, picker);

    // Re-observe if the picker element itself was replaced (e.g. combined-listing morph of `main`).
    if (!observers.has(picker)) {
      const observer = new MutationObserver(() => enhance(list, picker));
      observer.observe(picker, { childList: true, subtree: true });
      observers.set(picker, observer);
    }
  }
}

/** @param {import('@shopify/events').ProductSelectEvent} event */
async function handleProductSelect(event) {
  if (event.target instanceof Element && event.target.closest('product-card')) return;
  try {
    await event.promise;
  } catch {
    // Aborted by a newer selection: re-apply anyway.
  }
  run();
}

document.addEventListener(StandardEvents.productSelect, handleProductSelect);
document.addEventListener('shopify:section:load', run);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', run, { once: true });
} else {
  run();
}
